import React, { useEffect, useState } from "react";

const fmt  = (n, d=2) => `$${Number(n||0).toLocaleString("en-US",{minimumFractionDigits:d,maximumFractionDigits:d})}`;
const fmtn = (n, d=2) =>  Number(n||0).toLocaleString("en-US",{minimumFractionDigits:d,maximumFractionDigits:d});

const Badge = ({ s }) => {
  const map = {
    pending:        { bg:"var(--accent-soft)",  color:"var(--accent)",   label:"Pending" },
    paid:           { bg:"var(--success-soft)", color:"var(--success)",  label:"Paid" },
    no_invoice:     { bg:"var(--bg-elevated)",    color:"var(--text-muted)",label:"No Invoice" },
    invoice_created:{ bg:"var(--warning-soft)", color:"var(--warning)",  label:"Invoiced" },
    void:           { bg:"var(--danger-soft)",  color:"var(--danger)",   label:"Void" },
  };
  const st = map[s] || { bg:"var(--bg-elevated)", color:"var(--text-muted)", label: s };
  return <span style={{ background:st.bg, color:st.color, padding:"2px 8px",
    borderRadius:999, fontSize:11, fontWeight:600 }}>{st.label}</span>;
};

const FeeStructureCard = ({ config }) => {
  const perf    = config?.trader_performance_fee_pct ?? 25;
  const tshare  = config?.trader_split_trader_pct    ?? 80;
  const pshare  = 100 - tshare;
  return (
    <div className="card" style={{ marginBottom: 14, padding: 14 }}>
      <div style={{ fontSize: 13, fontWeight: 600, color: "var(--text-main)", marginBottom: 12 }}>Fee Structure</div>
      <div style={{display:"grid",gridTemplateColumns:"repeat(3,minmax(0,1fr))",gap:10,marginBottom:12}}>
        {[
          { label:"Performance Fee", value:`${fmtn(perf,0)}%`, sub:"of investor copy P&L",   color:"var(--accent)" },
          { label:"Your Share",      value:`${fmtn(tshare,0)}%`, sub:"of performance fee",   color:"var(--success)" },
          { label:"Platform Share",  value:`${fmtn(pshare,0)}%`, sub:"of performance fee",   color:"var(--text-muted)" },
        ].map((item,i)=>(
          <div key={i} style={{background:"var(--bg-elevated)",borderRadius:"var(--radius-md)",padding:"10px 12px"}}>
            <div style={{fontSize:11,color:"var(--text-muted)",marginBottom:4}}>{item.label}</div>
            <div style={{fontSize:18,fontWeight:700,color:item.color}}>{item.value}</div>
            <div style={{fontSize:10,color:"var(--text-faint)"}}>{item.sub}</div>
          </div>
        ))}
      </div>
      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(180px,1fr))",gap:10}}>
        {[
          { icon:"", title:"Performance-Based", desc:"You only earn when your investors profit above HWM." },
          { icon:"", title:"Monthly Settlement", desc:"Billing runs on the 1st of each month for the prior month." },
          { icon:"", title:"High Water Mark",    desc:"Investors only pay fees on new profits above their previous peak." },
          { icon:"", title:"Withdraw Anytime",   desc:"Once earnings are paid, request withdrawal anytime." },
        ].map((item,i)=>(
          <div key={i} style={{background:"var(--bg-elevated)",borderRadius:"var(--radius-md)",padding:"10px 12px",display:"flex",gap:8,alignItems:"flex-start"}}>
            <span style={{fontSize:16}}>{item.icon}</span>
            <div>
              <div style={{fontSize:12,fontWeight:600,color:"var(--text-main)",marginBottom:2}}>{item.title}</div>
              <div style={{fontSize:11,color:"var(--text-muted)",lineHeight:1.4}}>{item.desc}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const TraderEarnings = () => {
  const [data,        setData]        = useState(null);
  const [withdrawals, setWithdrawals] = useState([]);
  const [feeConfig,   setFeeConfig]   = useState({});
  const [loading,     setLoading]     = useState(true);
  const [withdrawAmt, setWithdrawAmt] = useState("");
  const [withdrawNotes, setWithdrawNotes] = useState("");
  const [submitting,  setSubmitting]  = useState(false);
  const [msg,         setMsg]         = useState(null);
  const [tab,         setTab]         = useState("breakdown"); // breakdown | monthly | withdrawals

  const token   = localStorage.getItem("access_token") || localStorage.getItem("token");
  const headers = { Authorization:"Bearer " + token };

  const load = () => {
    setLoading(true);
    Promise.all([
      fetch("/api/trader/earnings",             { headers }).then(r=>r.json()),
      fetch("/api/trader/earnings/withdrawals", { headers }).then(r=>r.json()),
      fetch("/api/public/fees").then(r=>r.json()),
    ]).then(([earn, wd, fees]) => {
      if (earn.ok) setData(earn.data);
      if (wd.ok)   setWithdrawals(wd.withdrawals || []);
      if (fees.ok) setFeeConfig(fees.fees || {});
    }).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const handleWithdraw = () => {
    const amt = parseFloat(withdrawAmt);
    if (!amt || amt <= 0) { setMsg({ type:"error", text:"Enter a valid amount" }); return; }
    setSubmitting(true);
    fetch("/api/trader/earnings/withdraw", {
      method:"POST",
      headers:{ ...headers, "Content-Type":"application/json" },
      body: JSON.stringify({ amount:amt, method:"bank_transfer", notes:withdrawNotes }),
    }).then(r=>r.json()).then(res => {
      if (res.ok) {
        setMsg({ type:"success", text:`Withdrawal request submitted. Available: $${fmtn(res.available)}` });
        setWithdrawAmt(""); setWithdrawNotes("");
        load();
      } else {
        setMsg({ type:"error", text:res.error || "Failed to submit" });
      }
    }).finally(() => setSubmitting(false));
  };

  if (loading) return (
    <div style={{ padding: "24px 0", maxWidth: 1200, margin: "0 auto" }}>
      <div style={{color:"var(--text-muted)",padding:24,textAlign:"center"}}>Loading earnings...</div>
    </div>
  );

  const available = (data?.paid_earnings || 0) - withdrawals
    .filter(w => w.status === "pending" || w.status === "processing")
    .reduce((s, w) => s + (w.amount || 0), 0);

  const TABS = [
    { id:"breakdown",   label:"Per Investor" },
    { id:"monthly",     label:"Monthly Chart" },
    { id:"withdrawals", label:"Withdrawals" },
  ];

  return (
    <div style={{ padding: "24px 0", maxWidth: 1200, margin: "0 auto", display: "flex", flexDirection: "column", gap: 14 }}>
      <div>
        <h1 style={{ fontSize: 22, fontWeight: 700, color: "var(--text-main)", margin: 0 }}>Earnings</h1>
        <p style={{ color: "var(--text-muted)", fontSize: 13, margin: "4px 0 0" }}>
          Performance fee earnings from your copy investors.
        </p>
      </div>

      <FeeStructureCard config={feeConfig} />

      {/* SUMMARY */}
      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(140px,1fr))",gap:12}}>
        {[
          { label:"Pending Earnings",  value:fmt(data?.pending_earnings),  color:"var(--accent)",   sub:"Invoiced, unpaid" },
          { label:"Paid Earnings",     value:fmt(data?.paid_earnings),     color:"var(--success)",  sub:"Already disbursed" },
          { label:"Total Earned",      value:fmt(data?.total_earned),      color:"var(--text-main)",sub:"Pending + Paid" },
          { label:"Active Investors",  value:data?.active_investors || 0,  color:"var(--text-main)",sub:"Currently copying" },
          { label:"Available Withdraw",value:fmt(Math.max(0,available)),   color:"var(--success)",  sub:"Paid minus pending WD" },
        ].map((c,i)=>(
          <div key={i} className="card" style={{padding:"14px 16px"}}>
            <div style={{fontSize:11,color:"var(--text-muted)",marginBottom:4}}>{c.label}</div>
            <div style={{fontSize:18,fontWeight:700,color:c.color}}>{c.value}</div>
            <div style={{fontSize:10,color:"var(--text-muted)",marginTop:2}}>{c.sub}</div>
          </div>
        ))}
      </div>

      {/* WITHDRAW */}
      <div className="card mb-4">
        <div className="card-header mb-3">
          <h2 className="card-title">Request Withdrawal</h2>
          <p className="card-meta" style={{fontSize:12}}>
            Available: <strong style={{color:"var(--accent)"}}>{fmt(Math.max(0,available))}</strong>
            {data?.pending_withdrawal > 0 && (
              <span style={{color:"var(--text-muted)",marginLeft:8}}>
                ({fmt(data.pending_withdrawal)} pending review)
              </span>
            )}
          </p>
        </div>
        {msg && (
          <div style={{padding:"10px 14px",borderRadius:"var(--radius-md)",marginBottom:12,fontSize:13,
            background:msg.type==="success"?"var(--success-soft)":"var(--danger-soft)",
            color:msg.type==="success"?"var(--success)":"var(--danger)"}}>
            {msg.text}
          </div>
        )}
        <div style={{display:"flex",gap:10,flexWrap:"wrap",alignItems:"flex-end"}}>
          <div style={{flex:"1 1 140px"}}>
            <label style={{fontSize:12,color:"var(--text-muted)",display:"block",marginBottom:4}}>Amount (USD)</label>
            <input type="number" min="1" step="0.01" value={withdrawAmt}
              onChange={e=>setWithdrawAmt(e.target.value)} placeholder="e.g. 100.00"
              style={{width:"100%",padding:"8px 12px",background:"var(--bg-elevated)",
                border:"1px solid var(--border)",borderRadius:"var(--radius-md)",color:"var(--text-main)",fontSize:14}}/>
          </div>
          <div style={{flex:"2 1 200px"}}>
            <label style={{fontSize:12,color:"var(--text-muted)",display:"block",marginBottom:4}}>Notes (bank info, etc)</label>
            <input type="text" value={withdrawNotes} onChange={e=>setWithdrawNotes(e.target.value)}
              placeholder="e.g. Bank Mandiri 1234567890"
              style={{width:"100%",padding:"8px 12px",background:"var(--bg-elevated)",
                border:"1px solid var(--border)",borderRadius:"var(--radius-md)",color:"var(--text-main)",fontSize:14}}/>
          </div>
          <button onClick={handleWithdraw} disabled={submitting || available <= 0}
            style={{padding:"10px 20px",background:"var(--accent)",color:"#fff",border:"none",
              borderRadius:"var(--radius-md)",fontWeight:600,fontSize:14,cursor:submitting||available<=0?"not-allowed":"pointer",
              opacity:submitting||available<=0?0.5:1,flexShrink:0}}>
            {submitting ? "Submitting..." : "Request Withdrawal"}
          </button>
        </div>
      </div>

      {/* TABS */}
      <div style={{display:"flex",gap:4,marginBottom:16,borderBottom:"1px solid var(--border)",paddingBottom:0}}>
        {TABS.map(t=>(
          <button key={t.id} onClick={()=>setTab(t.id)}
            style={{padding:"8px 18px",border:"none",cursor:"pointer",fontSize:13,fontWeight:600,
              background:"transparent",borderBottom:tab===t.id?"2px solid var(--accent)":"2px solid transparent",
              color:tab===t.id?"var(--accent)":"var(--text-muted)",marginBottom:-1}}>
            {t.label}
          </button>
        ))}
      </div>

      {/* TAB: PER INVESTOR */}
      {tab === "breakdown" && (
        <div className="card">
          <div className="card-header mb-3">
            <h2 className="card-title">Per Investor Breakdown</h2>
            <p className="card-meta" style={{fontSize:12}}>Fee snapshots per follower per month</p>
          </div>
          {(!data?.per_investor || data.per_investor.length === 0) ? (
            <div style={{color:"var(--text-muted)",fontSize:13,padding:"12px 0"}}>
              No earnings data yet. Billing runs on the 1st of each month.
            </div>
          ) : (
            <div style={{overflowX:"auto"}}>
              <table style={{width:"100%",borderCollapse:"collapse",fontSize:12}}>
                <thead>
                  <tr style={{borderBottom:"1px solid var(--border)"}}>
                    {["Invoice","Investor","Follower Acct","Month","Cum. P&L","Above HWM","Gross Fee","Your Share","Platform","Trades","Status"].map(h=>(
                      <th key={h} style={{padding:"8px 10px",textAlign:"left",fontSize:11,
                        fontWeight:700,color:"var(--text-muted)",textTransform:"uppercase",
                        letterSpacing:"0.04em",whiteSpace:"nowrap"}}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {data.per_investor.map((inv,i)=>(
                    <tr key={i} style={{borderBottom:"1px solid rgba(255,255,255,0.04)"}}>
                      <td style={{padding:"10px 10px",color:"var(--accent)",fontWeight:600,whiteSpace:"nowrap"}}>
                        {inv.invoice_number || "—"}
                      </td>
                      <td style={{padding:"10px 10px",color:"var(--text-main)",fontWeight:600}}>
                        {inv.investor_name}
                      </td>
                      <td style={{padding:"10px 10px",color:"var(--text-muted)",fontFamily:"monospace"}}>
                        {inv.follower_account}
                      </td>
                      <td style={{padding:"10px 10px",color:"var(--text-muted)",whiteSpace:"nowrap"}}>
                        {inv.billing_month}
                      </td>
                      <td style={{padding:"10px 10px",fontWeight:600,
                        color:inv.cumulative_pnl>=0?"var(--success)":"var(--danger)"}}>
                        {fmt(inv.cumulative_pnl)}
                      </td>
                      <td style={{padding:"10px 10px",color:"var(--accent)",fontWeight:600}}>
                        {fmt(inv.profit_above_hwm)}
                      </td>
                      <td style={{padding:"10px 10px",color:"var(--text-main)"}}>
                        {fmt(inv.gross_fee)}
                      </td>
                      <td style={{padding:"10px 10px",color:"var(--success)",fontWeight:700}}>
                        {fmt(inv.trader_share)}
                      </td>
                      <td style={{padding:"10px 10px",color:"var(--text-muted)"}}>
                        {fmt(inv.platform_share)}
                      </td>
                      <td style={{padding:"10px 10px",color:"var(--text-muted)",textAlign:"center"}}>
                        {inv.trade_count}
                      </td>
                      <td style={{padding:"10px 10px"}}>
                        <Badge s={inv.status} />
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr style={{borderTop:"2px solid var(--border)"}}>
                    <td colSpan={7} style={{padding:"10px 10px",fontSize:12,fontWeight:700,color:"var(--text-muted)"}}>
                      TOTAL
                    </td>
                    <td style={{padding:"10px 10px",fontSize:13,fontWeight:800,color:"var(--success)"}}>
                      {fmt(data.per_investor.reduce((s,r)=>s+(r.trader_share||0),0))}
                    </td>
                    <td style={{padding:"10px 10px",fontSize:12,fontWeight:700,color:"var(--text-muted)"}}>
                      {fmt(data.per_investor.reduce((s,r)=>s+(r.platform_share||0),0))}
                    </td>
                    <td colSpan={2}/>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </div>
      )}

      {/* TAB: MONTHLY CHART */}
      {tab === "monthly" && (
        <div className="card">
          <div className="card-header mb-3">
            <h2 className="card-title">Monthly Earnings</h2>
            <p className="card-meta" style={{fontSize:12}}>Last 12 months — your share after split</p>
          </div>
          {(!data?.monthly_chart || data.monthly_chart.length === 0) ? (
            <div style={{color:"var(--text-muted)",fontSize:13,padding:"12px 0"}}>No earnings data yet.</div>
          ) : (
            <div style={{display:"flex",flexDirection:"column",gap:8}}>
              {data.monthly_chart.map(m => {
                const max = Math.max(...data.monthly_chart.map(x=>x.trader_share));
                const pct = max > 0 ? (m.trader_share / max) * 100 : 0;
                return (
                  <div key={m.month} style={{display:"flex",alignItems:"center",gap:10}}>
                    <span style={{fontSize:12,color:"var(--text-muted)",width:65,flexShrink:0}}>{m.month}</span>
                    <div style={{flex:1,height:10,background:"var(--bg-elevated)",borderRadius:5,overflow:"hidden"}}>
                      <div style={{width:pct+"%",height:"100%",background:"var(--accent)",borderRadius:5,
                        transition:"width 0.3s ease"}}/>
                    </div>
                    <span style={{fontSize:12,color:"var(--success)",fontWeight:700,width:80,textAlign:"right"}}>
                      {fmt(m.trader_share)}
                    </span>
                    <span style={{fontSize:11,color:"var(--text-muted)",width:60,textAlign:"right"}}>
                      {m.followers} investor{m.followers!==1?"s":""}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* TAB: WITHDRAWALS */}
      {tab === "withdrawals" && (
        <div className="card">
          <div className="card-header mb-3">
            <h2 className="card-title">Withdrawal History</h2>
          </div>
          {withdrawals.length === 0 ? (
            <div style={{color:"var(--text-muted)",fontSize:13,padding:"12px 0"}}>No withdrawal requests yet.</div>
          ) : (
            <div style={{overflowX:"auto"}}>
              <table style={{width:"100%",borderCollapse:"collapse",fontSize:12}}>
                <thead>
                  <tr style={{borderBottom:"1px solid var(--border)"}}>
                    {["Amount","Method","Notes","Status","Requested","Processed"].map(h=>(
                      <th key={h} style={{padding:"8px 10px",textAlign:"left",fontSize:11,
                        fontWeight:700,color:"var(--text-muted)",textTransform:"uppercase",letterSpacing:"0.04em"}}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {withdrawals.map(w=>(
                    <tr key={w.id} style={{borderBottom:"1px solid rgba(255,255,255,0.04)"}}>
                      <td style={{padding:"10px 10px",fontWeight:700,color:"var(--text-main)"}}>{fmt(w.amount)}</td>
                      <td style={{padding:"10px 10px",color:"var(--text-muted)"}}>{w.method}</td>
                      <td style={{padding:"10px 10px",color:"var(--text-muted)",maxWidth:200}}>{w.notes||"—"}</td>
                      <td style={{padding:"10px 10px"}}><Badge s={w.status}/></td>
                      <td style={{padding:"10px 10px",color:"var(--text-muted)",whiteSpace:"nowrap"}}>{w.requested_at}</td>
                      <td style={{padding:"10px 10px",color:"var(--text-muted)",whiteSpace:"nowrap"}}>{w.processed_at||"—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default TraderEarnings;
